import React from "react"
import { makeAutoObservable } from "mobx"

import { Pagination } from "src/lib/pagination"
import { createLoadingKeys } from "src/lib/loading"
import { loads } from "src/channel/utils"
import { CommunityAdminService, PollAdminService } from "src/api"
import { Channel } from "src/channel"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { reportUnhandledApiError } from "src/lib/report"
import { IAdvanceQueryModel, IColumn } from "src/types/data-grid-pro"
import {
    IPostAndCommentsFilter,
    IPostInitialStateFilterItem,
    IRouteParams,
} from "src/views/community-posts-and-comments/types"
import { createItemsFromPost } from "src/views/community-posts-and-comments/helpers/createUnifiedItem"
import {
    getOperatorForField,
    handleDateFilter,
    parseFieldValue,
} from "src/helpers/filterHelpers"
import { IPostAndCommentsItem } from "src/types/community-posts-and-comments/communityPostsAndComments"
import { isPostItem } from "src/views/community-overview/helpers/communityHelpers"
import { getPollMaxEndDate } from "src/shared/polls/utils/pollMaxEndDate"

export class CommunityPostsAndCommentsStore implements IDisposable {
    //#region Observables
    static Context = React.createContext<CommunityPostsAndCommentsStore | null>(
        null,
    )
    static LoadingKeys = createLoadingKeys(
        "init",
        "loading",
        "delete-post",
        "loaded",
        "delete-comment",
        "duplicate-post",
    )

    private repositoryChangeListenerDisposer?: () => void
    private accessGroupId = DEFAULT_ACCESS_GROUP.id
    private _allPostIds: number[] = []

    gridInitialStatePro: IPostInitialStateFilterItem[] = []
    selectedFlagFilter: IPostAndCommentsFilter[] = []

    posts = new Pagination(
        async (params) => {
            const response =
                await CommunityAdminService.postV1AdminCommunityPostList({
                    request: {
                        flags:
                            this.selectedFlagFilter.length === 0
                                ? undefined
                                : this.selectedFlagFilter.map(
                                      (filter) => filter.id,
                                  ),
                        access_group_id: this.getAccessGroupId(),
                        page_number: params.page,
                        page_size: params.pageSize,
                        gridfilter: {
                            sort: params.advanceQuery?.sort,
                            items: params.advanceQuery?.items,
                            logic_operator: params.advanceQuery?.logicOperator,
                        },
                    },
                })

            // create unified items for post and comments
            const rows = (response.posts ?? []).flatMap(createItemsFromPost)

            this.setAllPostIds(
                response?.posts?.map((item) => item.post_id ?? 0) ?? [],
            )

            return {
                items: rows,
                count: response.total_count ?? 0,
            }
        },
        {
            loadingKey: CommunityPostsAndCommentsStore.LoadingKeys.loading,
        },
    )
    //#endregion

    get gridInitialState() {
        return this.gridInitialStatePro
    }

    get allPostIds() {
        return this._allPostIds
    }

    private setAllPostIds(allPostIds: number[]) {
        this._allPostIds = allPostIds
    }

    constructor() {
        makeAutoObservable(this)
    }

    //#region Async methods
    @loads(() => CommunityPostsAndCommentsStore.LoadingKeys.init)
    async init(accessGroupId: number, advanceQuery: IAdvanceQueryModel) {
        this.setAccessGroupId(accessGroupId)
        await this.posts.loadInitialPage(advanceQuery)
        this.listenToRepositoryChanges()
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.posts.loadAdvanceQuery(advanceQuery)
    }

    @loads(() => CommunityPostsAndCommentsStore.LoadingKeys["delete-post"])
    async deletePost(id: number): Promise<boolean> {
        try {
            await CommunityAdminService.deleteV1AdminCommunityPost({
                postId: id,
            })

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "community-posts-and-comments",
                    action: "delete",
                },
            })

            return true
        } catch (e) {
            reportUnhandledApiError(e)
            return false
        }
    }

    @loads(() => CommunityPostsAndCommentsStore.LoadingKeys["delete-comment"])
    async deleteComment(id: number | undefined) {
        if (id === undefined) return

        try {
            await CommunityAdminService.deleteV1AdminCommunityComment({
                commentId: id,
            })

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "community-posts-and-comments",
                    action: "update",
                },
            })
        } catch (e) {
            reportUnhandledApiError(e)
        }
    }

    @loads(() => CommunityPostsAndCommentsStore.LoadingKeys["duplicate-post"])
    async duplicatePost(id: number) {
        try {
            // Get the original post
            const post = await CommunityAdminService.getV1AdminCommunityPost1({
                postId: id,
            })

            // If post has a poll, duplicate it first
            let newPollId = undefined
            if (post.poll_id != null) {
                const pollResponse = await PollAdminService.getV1AdminPoll({
                    pollId: post.poll_id,
                })

                // Create new poll with a copy name
                const pollName = (pollResponse.name ?? "") + " (Copy)"
                const pollQuestion = (pollResponse.question ?? "") + " (Copy)"
                const publishPostAt =
                    post.publish_at != null ? new Date(post.publish_at) : null
                const newPoll = await PollAdminService.postV1AdminPoll({
                    request: {
                        question: pollQuestion,
                        name: pollName,
                        vote_options: (pollResponse.vote_options ?? []).map(
                            (opt) => ({ answer: opt.answer ?? "" }),
                        ),
                        close_at:
                            getPollMaxEndDate(publishPostAt).toISOString(),
                        vote_type: pollResponse.vote_type ?? "single",
                        access_group_id: pollResponse.access_group_id ?? 1,
                    },
                })

                newPollId = newPoll.poll_id
            }

            // Return the data needed to create the duplicate
            return { originalPost: post, newPollId }
        } catch (e) {
            reportUnhandledApiError(e)
            return null
        }
    }
    //#endregion

    private getAccessGroupId() {
        return this.accessGroupId !== DEFAULT_ACCESS_GROUP.id
            ? this.accessGroupId
            : undefined
    }

    //#region Private method
    private setAccessGroupId(accessGroupId: number) {
        this.accessGroupId = accessGroupId
    }

    private listenToRepositoryChanges() {
        this.repositoryChangeListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "community-posts-and-comments"
                ) {
                    await this.posts.reload()
                }
            },
        )
    }
    //#endregion

    //#region Public methods
    dispose() {
        this.repositoryChangeListenerDisposer?.()
    }

    canEditPost(postId: number, adminId?: number) {
        const post = this.posts.items.find(
            (item) => item.original_id === postId,
        )
        if (post == null) {
            return false
        }

        return (
            post.authorObjectType === "admin_user" &&
            post.authorObjectId === adminId
        )
    }

    setInitialStateFilters(filters: IPostInitialStateFilterItem[]) {
        this.gridInitialStatePro = filters
    }

    setFlagFilter(flagFilter: IPostAndCommentsFilter[]) {
        this.selectedFlagFilter = flagFilter
    }

    handleParams(
        columnHeaders: IColumn<IPostAndCommentsItem>[],
        params: IRouteParams[] = [],
    ): IPostInitialStateFilterItem[] {
        const filterableColumns = columnHeaders.filter(
            (column) =>
                column.filterable !== false && column.type !== undefined,
        )

        return params.flatMap((param) => {
            const column = filterableColumns.find(
                (col) => col.field === param.param,
            )
            if (column == null) return []

            const { field, type } = column

            try {
                if (type === "date" && field === "created_date") {
                    return handleDateFilter(param.value, field)
                }

                const value = parseFieldValue(field, param.value)
                const operator = getOperatorForField(field, type, value)

                return [
                    {
                        field: param.param,
                        operator,
                        value,
                    },
                ]
            } catch (error) {
                return []
            }
        })
    }

    canEditItem(item: IPostAndCommentsItem): boolean {
        return Boolean(isPostItem(item)) // Allow editing posts, not comments
    }
    //#endregion
}
